<script lang="ts">
	import { getContext } from 'svelte';
	import { _ } from 'svelte-i18n';
	import { slide } from 'svelte/transition';
	import { pressESC } from '../../../helper/Extensions';
	import IconButton from '../../buttons/IconButton.svelte';
	import InputLabel from '../../input/InputLabel.svelte';
	import {
		clearSavedFilterValues,
		ViewMode,
		type SimpleListConfiguration,
		type SimpleListContext,
		type SimpleListListFilter
	} from '../../../types/ListTypes';
	import DefaultBigFilter from '../filters/DefaultBigFilter.svelte';
	import DefaultBooleanFilter from '../filters/DefaultBooleanFilter.svelte';
	import DefaultDateFilter from '../filters/DefaultDateFilter.svelte';
	import DefaultNumberFilter from '../filters/DefaultNumberFilter.svelte';
	import DefaultOptionFilter from '../filters/DefaultOptionFilter.svelte';
	import DefaultStringFilter from '../filters/DefaultStringFilter.svelte';
	import DefaultTagFilter from '../filters/DefaultTagFilter.svelte';

	interface Props {
		listFilter: SimpleListListFilter;
		simpleListConfiguration: SimpleListConfiguration;
		showFilter: boolean;
	}

	let {
		listFilter = $bindable(),
		simpleListConfiguration = $bindable(),
		showFilter = $bindable()
	}: Props = $props();

	const { updateFilterActivityState } = getContext<SimpleListContext>('enzianListContext');

	//How many active filters do we have. This is the counter that is shown in the blue bubble.
	let activeFilters: number = $derived.by(() => {
		//Reset the count
		let activeFilters = 0;

		listFilter.filterOptions.forEach((option) => {
			const isShown = option.showAsFilter != null ? option.showAsFilter : true;

			//For showing how many filters are active we only check if the value is not null
			if (option.value != null && isShown) {
				return (activeFilters += 1);
			}
		});

		return activeFilters;
	});

	let activeFiltersWithoutResetProtected: number = $derived.by(() => {
		//Reset the count
		let activeFiltersWithoutResetProtected = 0;

		listFilter.filterOptions.forEach((option) => {
			const isShown = option.showAsFilter != null ? option.showAsFilter : true;

			//Has this filter a value? In case there is a default value set we have to check if its not the same as the value
			const hasValue =
				option.defaultValue != undefined
					? option.defaultValue != option.value
					: option.value != null;

			if (!option.resetProtect && isShown && hasValue) {
				return (activeFiltersWithoutResetProtected += 1);
			}
		});

		return activeFiltersWithoutResetProtected;
	});

	let filterElement = $state();

	//Do we have an or seach term active?
	let orSearchActive: boolean = $derived((listFilter.searchBarInput?.length || 0) > 0);

	function handleClearFilter() {
		//Clear the filters
		clearSavedFilterValues(listFilter);
		//Inform parent
		updateFilterActivityState();
	}
</script>

<div
	class="relative"
	use:pressESC={() => {
		showFilter = false;
	}}
	class:mt-6={simpleListConfiguration.header == undefined && listFilter.showFilterIcon}
	class:mb-8={simpleListConfiguration.viewMode == ViewMode.LIST && listFilter.showFilterIcon}
>
	<div>
		{#if listFilter.filterOptions.length > 0}
			<!--Filters-->
			{#if showFilter && !orSearchActive}
				<div
					transition:slide
					bind:this={filterElement}
					class="px-6 pt-6 pb-8 bg-white dark:bg-dark-300 rounded-br-[15px] grid gap-2"
					style={'grid-template-columns: repeat(' + listFilter.gridCols + ', minmax(0, 1fr))'}
				>
					{#each listFilter.filterOptions as filterOption, index}
						{#if filterOption.showAsFilter != null ? filterOption.showAsFilter : true}
							<div>
								<InputLabel label={filterOption.label} />

								{#if filterOption.type == 'string'}
									<DefaultStringFilter bind:filterOption={listFilter.filterOptions[index]} />
								{:else if filterOption.type == 'big'}
									<DefaultBigFilter bind:filterOption={listFilter.filterOptions[index]} />
								{:else if filterOption.type == 'number'}
									<DefaultNumberFilter
										hasFilter={true}
										bind:filterOption={listFilter.filterOptions[index]}
									/>
								{:else if filterOption.type == 'boolean'}
									<DefaultBooleanFilter bind:filterOption={listFilter.filterOptions[index]} />
								{:else if filterOption.type == 'date'}
									<DefaultDateFilter
										propertyName={filterOption.key || ''}
										bind:filterOption={listFilter.filterOptions[index]}
									/>
								{:else if filterOption.type == 'tags'}
									<DefaultTagFilter bind:filterOption={listFilter.filterOptions[index]} />
								{:else if filterOption.type == 'options' && filterOption.optionsOption}
									<DefaultOptionFilter bind:filterOption={listFilter.filterOptions[index]} />
								{/if}
							</div>
						{/if}
					{/each}
				</div>
			{/if}
		{/if}
	</div>

	<!--Filter icon-->
	{#if listFilter.filterOptions.length > 0 && listFilter.showFilterIcon}
		<div
			class="absolute left-4 bottom-0 translate-y-1/2 flex rounded-full dark:bg-dark-300 p-4 gap-2 items-center"
			class:bg-white={simpleListConfiguration.viewMode != ViewMode.LIST}
		>
			<!--Button-->
			<div class="relative flex justify-center">
				<IconButton
					disabled={orSearchActive}
					hoverText={$_('basic_components.bt_filter')}
					size={'small'}
					iconName="filter"
					onClick={() => {
						showFilter = !showFilter;
					}}
				/>

				<!-- Badge -->
				{#if activeFilters > 0}
					<span
						class="left-1/2 bottom-1/2 text-xs font-bold absolute bg-secondary-300 dark:bg-secondary-500 text-white h-5 w-5 rounded-full z-40 text-center pointer-events-none"
					>
						{activeFilters}
					</span>
				{/if}
			</div>

			<!--Clear button-->
			<IconButton
				disabled={activeFiltersWithoutResetProtected <= 0 || orSearchActive}
				hoverText={$_('basic_components.bt_filter_clear')}
				size={'small'}
				iconName="eraser"
				onClick={handleClearFilter}
			/>
		</div>
	{/if}
</div>
